// ts-rest contract for the COMPLIANCE side of PA Dealing (SEP-264, SEP-270):
// the review queue and the request detail with its decisions, the Restricted
// List with its history, the engine rules, the question settings, the
// questionnaire assignments and the audit search.
//
// Every route here is gated to `comply` (a granted admin passes through the
// admin bypass). A non-Compliance caller gets 403 from the route guard, before
// any handler runs.
//
// One route is raw Nest and not ts-rest, because it streams a file:
//   GET /api/pa-dealing-review/audit/export.csv   the filtered audit set as CSV

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
    clonePaRestrictionBodySchema,
    paAuditQuerySchema,
    paAuditResponseSchema,
    paCommentBodySchema,
    paCommentSchema,
    paCommentVisibilityBodySchema,
    paComplianceWithdrawBodySchema,
    paDecisionBodySchema,
    paExtendClearanceBodySchema,
    paOptionalReasonBodySchema,
    paQuestionSettingsResponseSchema,
    paQuestionnaireAssignmentsResponseSchema,
    paReasonBodySchema,
    paRejectBodySchema,
    paRequestInformationBodySchema,
    paRestrictionDetailResponseSchema,
    paRestrictionFieldsBodySchema,
    paRestrictionSchema,
    paRestrictionVersionsResponseSchema,
    paRestrictionsQuerySchema,
    paRestrictionsResponseSchema,
    paReviewQueueQuerySchema,
    paReviewQueueResponseSchema,
    paRuleSchema,
    paRulesResponseSchema,
    paQuestionSettingSchema,
    paQuestionnaireAssignmentSchema,
    paStaffResponseSchema,
    paToggleBodySchema,
    paTradeRequestReviewSchema,
    setPaQuestionnaireAssignmentBodySchema,
    updatePaRestrictionBodySchema,
} from '../schemas/pa-dealing';
import { errorBody, errorBodyWithCode } from '../schemas/common';

const c = initContract();

const idParams = z.object({ id: z.string() });
const decisionResponses = {
    200: paTradeRequestReviewSchema,
    403: errorBody,
    404: errorBody,
    409: errorBodyWithCode,
};

export const paDealingReviewContract = c.router(
    {
        // --- The queue and the request ---
        listQueue: {
            method: 'GET',
            path: '/pa-dealing-review/requests',
            query: paReviewQueueQuerySchema,
            responses: { 200: paReviewQueueResponseSchema, 403: errorBody },
            summary: 'The review queue: every submitted request, with the SEP-270 filters',
        },
        getRequest: {
            method: 'GET',
            path: '/pa-dealing-review/requests/:id',
            pathParams: idParams,
            responses: { 200: paTradeRequestReviewSchema, 403: errorBody, 404: errorBody },
            summary:
                'The full record of one request: snapshot, rules, flags, matches, timeline, comments',
        },
        approve: {
            method: 'POST',
            path: '/pa-dealing-review/requests/:id/approve',
            pathParams: idParams,
            body: paDecisionBodySchema,
            responses: decisionResponses,
            summary: 'Approve a pending request; the clearance ends at the end of the London day',
        },
        reject: {
            method: 'POST',
            path: '/pa-dealing-review/requests/:id/reject',
            pathParams: idParams,
            body: paRejectBodySchema,
            responses: decisionResponses,
            summary: 'Reject a pending request with a note',
        },
        requestInformation: {
            method: 'POST',
            path: '/pa-dealing-review/requests/:id/request-information',
            pathParams: idParams,
            body: paRequestInformationBodySchema,
            responses: decisionResponses,
            summary: 'Ask the employee for more information',
        },
        withdraw: {
            method: 'POST',
            path: '/pa-dealing-review/requests/:id/withdraw',
            pathParams: idParams,
            body: paComplianceWithdrawBodySchema,
            responses: decisionResponses,
            summary: 'Withdraw a pending or information-requested request, with a reason',
        },
        extendClearance: {
            method: 'POST',
            path: '/pa-dealing-review/requests/:id/extend-clearance',
            pathParams: idParams,
            body: paExtendClearanceBodySchema,
            responses: decisionResponses,
            summary: 'Extend a live clearance to the end of a later London day, with a reason',
        },
        addComment: {
            method: 'POST',
            path: '/pa-dealing-review/requests/:id/comments',
            pathParams: idParams,
            body: paCommentBodySchema,
            responses: {
                201: paCommentSchema,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Comment on a request, employee-visible or internal',
        },
        changeCommentVisibility: {
            method: 'PATCH',
            path: '/pa-dealing-review/comments/:id/visibility',
            pathParams: idParams,
            body: paCommentVisibilityBodySchema,
            responses: {
                200: paCommentSchema,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Change the visibility of one comment (audited)',
        },

        // --- The Restricted List ---
        listRestrictions: {
            method: 'GET',
            path: '/pa-dealing-review/restrictions',
            query: paRestrictionsQuerySchema,
            responses: { 200: paRestrictionsResponseSchema, 403: errorBody },
            summary: 'The Restricted List, active rows by default',
        },
        getRestriction: {
            method: 'GET',
            path: '/pa-dealing-review/restrictions/:id',
            pathParams: idParams,
            responses: { 200: paRestrictionDetailResponseSchema, 403: errorBody, 404: errorBody },
            summary: 'One restriction with its version history',
        },
        createRestriction: {
            method: 'POST',
            path: '/pa-dealing-review/restrictions',
            body: paRestrictionFieldsBodySchema,
            responses: {
                201: paRestrictionSchema,
                400: errorBodyWithCode,
                403: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Add a restriction',
        },
        updateRestriction: {
            method: 'PATCH',
            path: '/pa-dealing-review/restrictions/:id',
            pathParams: idParams,
            body: updatePaRestrictionBodySchema,
            responses: {
                200: paRestrictionSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Edit a restriction; the version grows and the history keeps the old values',
        },
        disableRestriction: {
            method: 'POST',
            path: '/pa-dealing-review/restrictions/:id/disable',
            pathParams: idParams,
            body: paReasonBodySchema,
            responses: {
                200: paRestrictionSchema,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Disable a restriction with a reason',
        },
        enableRestriction: {
            method: 'POST',
            path: '/pa-dealing-review/restrictions/:id/enable',
            pathParams: idParams,
            body: paOptionalReasonBodySchema,
            responses: {
                200: paRestrictionSchema,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Enable a disabled restriction',
        },
        cloneRestriction: {
            method: 'POST',
            path: '/pa-dealing-review/restrictions/:id/clone',
            pathParams: idParams,
            body: clonePaRestrictionBodySchema,
            responses: {
                201: paRestrictionSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Create a new restriction from a copy of this one, with overrides',
        },
        deleteRestriction: {
            method: 'DELETE',
            path: '/pa-dealing-review/restrictions/:id',
            pathParams: idParams,
            body: paOptionalReasonBodySchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
                403: errorBodyWithCode,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary:
                'Hard-delete a restriction (admin only, and only when no request ever matched it)',
        },
        listRestrictionVersions: {
            method: 'GET',
            path: '/pa-dealing-review/restrictions/:id/versions',
            pathParams: idParams,
            responses: { 200: paRestrictionVersionsResponseSchema, 403: errorBody },
            summary:
                'The version history of one restriction, newest first (a deleted row keeps its history)',
        },

        // --- The rules and the questions ---
        listRules: {
            method: 'GET',
            path: '/pa-dealing-review/rules',
            responses: { 200: paRulesResponseSchema, 403: errorBody },
            summary: 'The five engine rules, in evaluation order',
        },
        toggleRule: {
            method: 'POST',
            path: '/pa-dealing-review/rules/:id/toggle',
            pathParams: idParams,
            body: paToggleBodySchema,
            responses: {
                200: paRuleSchema,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Switch one rule on or off, with a reason',
        },
        listQuestionSettings: {
            method: 'GET',
            path: '/pa-dealing-review/questions',
            responses: { 200: paQuestionSettingsResponseSchema, 403: errorBody },
            summary: 'Every question with its on or off state for each questionnaire kind',
        },
        toggleQuestionSetting: {
            method: 'POST',
            path: '/pa-dealing-review/questions/:id/toggle',
            pathParams: idParams,
            body: paToggleBodySchema,
            responses: {
                200: paQuestionSettingSchema,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Switch one question on or off for one kind, with a reason',
        },

        // --- The questionnaire assignments ---
        listQuestionnaireAssignments: {
            method: 'GET',
            path: '/pa-dealing-review/questionnaire-assignments',
            responses: { 200: paQuestionnaireAssignmentsResponseSchema, 403: errorBody },
            summary:
                'Every active staff member with their default, override and effective questionnaire kind',
        },
        setQuestionnaireAssignment: {
            method: 'PUT',
            path: '/pa-dealing-review/questionnaire-assignments/:id',
            pathParams: idParams,
            body: setPaQuestionnaireAssignmentBodySchema,
            responses: { 200: paQuestionnaireAssignmentSchema, 403: errorBody, 404: errorBody },
            summary: 'Override, or clear the override of, the questionnaire kind of one user',
        },
        listStaff: {
            method: 'GET',
            path: '/pa-dealing-review/staff',
            responses: { 200: paStaffResponseSchema, 403: errorBody },
            summary: 'The active staff directory, for the user and monitored-by pickers',
        },

        // --- The audit search ---
        searchAudit: {
            method: 'GET',
            path: '/pa-dealing-review/audit',
            query: paAuditQuerySchema,
            responses: { 200: paAuditResponseSchema, 403: errorBody },
            summary: 'Search the PA Dealing audit events; the CSV export takes the same filters',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
